import { z } from 'zod';

/**
 * The « Notes » section of an export (issue #19, `docs/specs/matrices.md`
 * « Export »): the matrix memo, then the open discussions of the Client level
 * with their target, author, date and replies - already worded and dated for
 * the page, so that the HTML, PDF and Excel renderers and the export page's
 * previews all print the same text.
 *
 * Built by `buildExportNotes` from what the backend loads; served as-is by
 * `GET /matrices/:id/export-notes` so the previews match the file.
 */

/** One note or reply as the page prints it: « Auteur, date - texte ». */
export const exportNotesMessageSchema = z.object({
  /** Short display name (« Alice P. »), or a stand-in once the account is gone. */
  author: z.string(),
  /** The day, worded for a document (« 28 sept. 2026 »). */
  date: z.string(),
  text: z.string(),
});
export type ExportNotesMessage = z.infer<typeof exportNotesMessageSchema>;

/** A discussion: its target (printed in bold), the root note and the replies, oldest first. */
export const exportNotesDiscussionSchema = z.object({
  target: z.string(),
  message: exportNotesMessageSchema,
  replies: z.array(exportNotesMessageSchema),
});
export type ExportNotesDiscussion = z.infer<typeof exportNotesDiscussionSchema>;

export const exportNotesSectionSchema = z.object({
  /** The memo, null when it is empty. */
  memo: z.string().nullable(),
  discussions: z.array(exportNotesDiscussionSchema),
});
export type ExportNotesSection = z.infer<typeof exportNotesSectionSchema>;

/**
 * `GET /matrices/:id/export-notes`: the section an export with « Inclure les
 * notes de niveau Client » would carry, `null` when there is nothing to carry
 * (no memo and no open Client discussion).
 */
export const exportNotesResponseSchema = z.object({
  section: exportNotesSectionSchema.nullable(),
});
export type ExportNotesResponse = z.infer<typeof exportNotesResponseSchema>;
